import { createRootRoute, Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { authClient } from '../lib/auth'
import { m } from '../paraglide/messages'

export const Route = createRootRoute({ component: RootLayout })

const NAV_LINKS = [
  { to: '/lessons',   label: () => m.nav_lessons() },
  { to: '/students',  label: () => m.nav_students() },
  { to: '/messages',  label: () => m.nav_messages() },
  { to: '/templates', label: () => m.nav_templates() },
  { to: '/jobs',      label: () => m.nav_jobs() },
  { to: '/settings',  label: () => m.nav_settings() },
] as const

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="text-sm text-zinc-600 hover:text-zinc-900 pb-0.5"
      activeProps={{ className: 'text-sm font-semibold border-b rounded-none border-zinc-900 text-zinc-900 pb-0.5' }}
      activeOptions={{ exact: false }}
    >
      {children}
    </Link>
  )
}

function RootLayout() {
  const location  = useLocation()
  const navigate  = useNavigate()
  // Bare (chrome-less) page: only the login screen. Confirmation now lives
  // inside the authorized app, so it keeps the normal navigation chrome.
  const isBare    = location.pathname === '/login'
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => { setMenuOpen(false) }, [location.pathname])

  const handleSignOut = async () => {
    await authClient.signOut()
    navigate({ to: '/login' })
  }

  if (isBare) return <Outlet />

  return (
    <div className="min-h-dvh bg-white">
      <nav className="sticky top-0 z-10 bg-white border-b border-zinc-200">
        {/* Main bar */}
        <div className="max-w-5xl mx-auto px-4 h-12 flex items-center justify-between gap-4">
          <Link to="/lessons" className="text-sm font-semibold text-zinc-900 shrink-0">
            {m.app_name()} 
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-6 flex-1">
            {NAV_LINKS.map(({ to, label }) => (
              <NavLink key={to} to={to}>{label()}</NavLink>
            ))}
          </div>

          {/* Desktop actions */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <button
              onClick={handleSignOut}
              className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100"
            >
              {m.sign_out()}
            </button>
          </div>

          {/* Mobile menu toggle */}
          <div className="flex md:hidden items-center gap-2 shrink-0">
            <button
              onClick={() => setMenuOpen(o => !o)}
              className="border border-zinc-900 px-2.5 py-1 text-sm hover:bg-zinc-100"
              aria-label={m.toggle_menu()}
            >
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {menuOpen && (
          <div className="md:hidden border-t border-zinc-200 bg-white px-4 py-4 flex flex-col gap-4">
            {NAV_LINKS.map(({ to, label }) => (
              <NavLink key={to} to={to}>{label()}</NavLink>
            ))}
            <button
              onClick={handleSignOut}
              className="text-start text-sm text-zinc-600 hover:text-zinc-900"
            >
              {m.sign_out()}
            </button>
          </div>
        )}
      </nav>

      <main className="max-w-5xl mx-auto px-4 py-6">
        <Outlet />
      </main>
    </div>
  )
}
